import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Mic, Square, Loader2, FileText, Sparkles, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VoiceAI = () => {
  const { getAuthHeaders, subscription, refreshSubscription } = useAuth();
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [articleDraft, setArticleDraft] = useState('');
  const [keywords, setKeywords] = useState('');
  const [error, setError] = useState('');
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const isLocked = !subscription?.voice_enabled;

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { audioChunksRef.current.push(e.data); };
      mediaRecorder.onstop = () => { stream.getTracks().forEach(track => track.stop()); };
      mediaRecorder.start();
      setRecording(true);
      setError('');
    } catch (err) {
      setError('Could not access microphone. Please allow microphone permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      processAudio();
    }
  };

  const processAudio = async () => {
    setProcessing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 100));
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      if (keywords) formData.append('keywords', keywords);
      const response = await axios.post(`${API}/voice/transcribe`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setTranscript(response.data.transcript);
      setArticleDraft(response.data.article_draft);
      await refreshSubscription();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to process audio');
    } finally {
      setProcessing(false);
    }
  };

  if (isLocked) {
    return (
      <div className="fade-in" data-testid="voice-page-locked">
        <div className="text-center py-20">
          <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 bg-[var(--color-surface)]">
            <Lock className="w-10 h-10 text-[var(--color-muted)]" />
          </div>
          <h1 className="font-serif text-3xl mb-3">Voice AI</h1>
          <p className="text-sm text-[var(--color-muted)] max-w-md mx-auto mb-8">
            Speak your ideas for 2 minutes and get a full, SEO-optimized article. 
            Available on Professional plan and above.
          </p>
          <div className="card inline-block p-7 text-left">
            <p className="font-display font-medium text-sm mb-3">What you get with Voice AI:</p>
            <ul className="text-xs text-[var(--color-muted)] space-y-2">
              <li>&#8226; Speak naturally, no scripting needed</li>
              <li>&#8226; AI transcribes and expands your ideas</li>
              <li>&#8226; SEO-optimized output ready to publish</li>
              <li>&#8226; Preserves your authentic voice</li>
            </ul>
            <button className="btn btn-accent w-full mt-5 text-sm">
              Upgrade to Professional (&#8364;79/mo)
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in" data-testid="voice-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="font-serif text-3xl mb-2">Voice to Article</h1>
          <p className="text-sm text-[var(--color-muted)]">
            Speak your ideas. We'll turn them into a full article. (5 credits per session)
          </p>
        </div>

        <div className="mb-6">
          <label className="block text-xs font-medium mb-1.5">Keywords (optional)</label>
          <input type="text" value={keywords} onChange={(e) => setKeywords(e.target.value)}
            className="input" placeholder="SEO, content marketing, small business"
            disabled={recording || processing} data-testid="voice-keywords-input" />
          <p className="text-[10px] text-[var(--color-muted)] mt-1">Comma-separated keywords to include in your article</p>
        </div>

        <div className="voice-recording mb-8">
          {error && (
            <div className="mb-4 p-3 rounded-xl text-xs bg-red-50 text-red-700">{error}</div>
          )}
          <button
            onClick={recording ? stopRecording : startRecording}
            disabled={processing}
            className={`voice-button ${recording ? 'recording' : ''}`}
            data-testid="voice-record-btn"
          >
            {processing ? <Loader2 className="w-10 h-10 animate-spin" /> : recording ? <Square className="w-10 h-10" /> : <Mic className="w-10 h-10" />}
          </button>
          <p className="mt-5 text-center text-sm text-[var(--color-muted)]">
            {processing ? 'Processing your voice...' : recording ? 'Recording... Click to stop' : 'Click to start recording'}
          </p>
          <p className="mt-1.5 text-[10px] text-[var(--color-muted)] text-center">
            Speak for about 2 minutes describing your article idea
          </p>
        </div>

        {transcript && (
          <div className="space-y-5">
            <div className="card p-6">
              <h3 className="font-display text-sm font-medium mb-3 flex items-center gap-2">
                <Mic className="w-4 h-4 text-[var(--color-coral)]" /> Your Transcript
              </h3>
              <p className="text-xs text-[var(--color-muted)] whitespace-pre-wrap leading-relaxed">{transcript}</p>
            </div>
            <div className="card p-6">
              <h3 className="font-display text-sm font-medium mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[var(--color-coral)]" /> Generated Article
              </h3>
              <div className="prose prose-sm max-w-none text-sm" dangerouslySetInnerHTML={{ __html: articleDraft }} />
              <div className="mt-6 flex gap-3">
                <button className="btn btn-primary flex-1 text-sm"><FileText className="w-4 h-4" /> Save as Draft</button>
                <button className="btn btn-secondary flex-1 text-sm">Edit in Editor</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceAI;
